import React, { useRef, useState } from 'react';

import noop from '../utils/noop';
import { useTextFieldStyles } from './SearchBox.styles';
import { TextField } from '../TextField';
import type { SearchBoxProps } from './SearchBox.types';
import { useIntl } from 'react-intl';

export const SearchBox = (props: SearchBoxProps) => {
    const {
        value = '',
        onClear = noop,
        onEnter,
        onBlur,
        classes: classesProp,
        InputProps: InputPropsProp,
        canExpand = false,
        isMobile,
        ...others
    } = props;
    const { formatMessage } = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isExpanded, setIsExpanded] = useState(!canExpand);

    const classes = useTextFieldStyles({
        isExpanded,
        canExpand,
        isMobile
    });

    const handleClear = () => {
        onClear();
        inputRef.current?.focus();
    };

    const handleFocus = () => {
        setIsExpanded(true);
        props.onClick?.();
        inputRef.current?.focus();
    };

    const handleMouseOver = () => {
        canExpand && handleFocus();
    };

    const handleMouseLeave = () => {
        canExpand && setIsExpanded(!!value);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (onEnter && event.key === 'Enter') {
            if (value) {
                onEnter();
            } else {
                // Remove focus when Enter is pressed without any search input, especially useful on mobile devices.
                inputRef.current?.blur();
            }
        }
    };

    const placeholder = formatMessage({
        id: 'common.header.search-placeholder'
    });

    return (
        <TextField
            classes={classes}
            icon="search"
            hideClearIcon={value.length === 0}
            value={value}
            onClear={handleClear}
            onKeyDown={onKeyDown}
            onFocus={handleFocus}
            onMouseDown={handleFocus}
            onClick={handleFocus}
            onBlur={onBlur}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
            data-testid="header-search-input"
            InputProps={{
                ...InputPropsProp,
                inputRef,
                classes: {
                    input: classes.input
                },
                placeholder
            }}
            {...others}
        />
    );
};
